import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { AuthService } from './auth/auth.service';
import { FrontendLoggerService } from './frontend-logger.service';
import { NotificationStateStore } from './notification-state.store';
import { NotificationWebSocketClient } from './notification-websocket.client';

export interface Notification {
  id: string;
  type: 'info' | 'alert' | 'success' | 'warning';
  message: string;
  priority?: 'low' | 'medium' | 'high';
  timestamp: Date;
  read: boolean;
  data?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  public readonly notifications$: Observable<Notification[]>;
  public readonly transient$: Observable<Notification>;
  public readonly unreadCount$: Observable<number>;

  private readonly stateStore: NotificationStateStore;
  private readonly socketClient: NotificationWebSocketClient;

  constructor(
    http: HttpClient,
    ngZone: NgZone,
    private readonly authService: AuthService,
    apiService: ApiService,
    private readonly frontendLogger: FrontendLoggerService
  ) {
    this.stateStore = new NotificationStateStore(http, environment.apiUrl, frontendLogger);
    this.socketClient = new NotificationWebSocketClient(
      apiService,
      ngZone,
      frontendLogger,
      this.stateStore
    );
    this.notifications$ = this.stateStore.notifications$;
    this.transient$ = this.stateStore.transient$;
    this.unreadCount$ = this.stateStore.unreadCount$;
    this.initializeAfterAuthReady();
  }

  showTransient(message: string, type: Notification['type'] = 'info'): void {
    this.stateStore.showTransient(message, type);
  }

  pushLocal(
    type: Notification['type'],
    message: string,
    data?: Record<string, unknown>
  ): void {
    this.stateStore.pushLocal(type, message, data);
  }

  connectWebSocket(): void {
    this.socketClient.connect();
  }

  buildSocketUrl(wsUrl: string, ticket: string): string | null {
    return this.socketClient.buildSocketUrl(wsUrl, ticket);
  }

  resolveWsUrl(wsUrl: string): URL {
    return this.socketClient.resolveWsUrl(wsUrl);
  }

  getWsBaseUrl(): string {
    return this.socketClient.getWsBaseUrl();
  }

  disconnect(): void {
    this.socketClient.disconnect();
  }

  loadNotifications(): void {
    this.stateStore.loadNotifications();
  }

  updateUnreadCount(): void {
    this.stateStore.updateUnreadCount();
  }

  getNotifications(): Observable<any[]> {
    return this.stateStore.getNotifications();
  }

  markAsRead(id: string): void {
    this.stateStore.markAsRead(id);
  }

  markAllAsRead(): void {
    this.stateStore.markAllAsRead();
  }

  addNotification(notification: Partial<Notification>): void {
    this.stateStore.addNotification(notification);
  }

  private initializeAfterAuthReady(): void {
    const loading$ = this.authService.isLoading$ as Observable<boolean>;
    combineLatest([loading$, this.authService.isAuthenticated$]).pipe(
      filter(([isLoading, isAuthenticated]) => !isLoading && isAuthenticated),
      take(1)
    ).subscribe(() => {
      this.frontendLogger.info(
        '[NotificationService] Auth establecida. Inicializando notificaciones y WebSocket.'
      );
      this.loadNotifications();
      this.connectWebSocket();
    });
  }
}
